import React, { useState, useEffect } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { OVERVIEW, GAPS, KEYWORD_MAP } from "../../../../../core/theme/reportTheme";
import { CHANNEL_NAMES } from "../../../../../core/data/marketOverviewText";
import { keywordMap, competitionLevel } from "../../../../../core/data/gaps";
import { loadPostcodeShapes, BOUNDARIES_CREDIT } from "../../../../../core/api/boundariesApi";
import { useFoldingCredit, NoLeafletPrefix } from "../../../../../core/layout/mapCredit";

const CHANNELS = ["ppc", "local", "shopping", "seo"].map(c => [c, CHANNEL_NAMES[c]]);
const UK_CENTRE = [54.2, -2.5];
const UK_ZOOM = 6;
const TILE_CREDIT = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// "Gaps by keyword": a map of the searched postcodes, each area coloured by
// how many different advertisers were seen there for the chosen keyword and
// channel. Click an area for every channel. Follows the report filters.
export default function KeywordMapPanel({ result, expandButton, expanded }) {
  const [keyword, setKeyword] = useState(null); // null = all keywords
  const [channel, setChannel] = useState("ppc");
  const keywords = result.gapSummary.keywordsBySearches.map(k => k.keyword);
  const chosen = keyword && keywords.includes(keyword) ? keyword : null; // a filter may remove it
  const { areas, summary } = keywordMap(result, { keyword: chosen, channel });
  const { shapes, loading } = usePostcodeShapes(areas.map(a => a.postcode));
  const placed = areas.filter(a => shapes[a.postcode]);
  const channelName = CHANNEL_NAMES[channel].toLowerCase();
  const [creditFolded, foldCredit] = useFoldingCredit();

  return (
    <div className={`${OVERVIEW.panel} flex-1 overflow-hidden`}>
      {/* One row: title, keyword and channel choices, expand - so the map gets the room. */}
      <div className={KEYWORD_MAP.header}>
        <h3 className={OVERVIEW.panelTitle}>Gaps by keyword</h3>
        <div className={KEYWORD_MAP.controls}>
          <select className={KEYWORD_MAP.keywordSelect} value={chosen || ""} onChange={e => setKeyword(e.target.value || null)}>
            <option value="">All keywords ({keywords.length})</option>
            {keywords.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <div className={GAPS.toggle}>
            {CHANNELS.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setChannel(id)} className={channel === id ? GAPS.toggleActive : GAPS.toggleButton} title={CHANNEL_NAMES[id]}>{label}</button>
            ))}
          </div>
        </div>
        {expandButton}
      </div>
      <div className={`${KEYWORD_MAP.mapArea} ${creditFolded ? "map-credit-folded" : ""}`} onPointerDown={foldCredit} onWheel={foldCredit}>
        <MapContainer center={UK_CENTRE} zoom={UK_ZOOM} style={{ height: "100%", width: "100%" }} scrollWheelZoom={expanded}>
          <NoLeafletPrefix />
          <TileLayer attribution={`${TILE_CREDIT} · ${BOUNDARIES_CREDIT}`} url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {placed.map(area => (
            <Area key={`${area.postcode}|${chosen}|${channel}|${area.value}|${area.searches}`} area={area} shape={shapes[area.postcode]} keyword={chosen} />
          ))}
          <FitToAreas shapes={placed.map(a => shapes[a.postcode])} expanded={expanded} />
        </MapContainer>
        {loading && <div className={KEYWORD_MAP.overlay}>Loading postcode areas…</div>}
        {!loading && areas.length > 0 && placed.length === 0 && <div className={KEYWORD_MAP.overlay}>Couldn't find these postcodes on the map.</div>}
        {areas.length === 0 && <div className={KEYWORD_MAP.overlay}>No postcodes for these filters.</div>}
      </div>
      <div className={KEYWORD_MAP.legendRow}>
        <span>No {channelName} in <span className={GAPS.summaryStrong}>{summary.empty} of {summary.postcodes}</span> postcodes ·</span>
        <span>Different advertisers in {channelName}:</span>
        {KEYWORD_MAP.legend.map(([level, label]) => (
          <span key={level}>
            <span className={KEYWORD_MAP.swatch} style={swatchStyle(KEYWORD_MAP.levels[level])} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// One postcode: its outline, or a circle for a full postcode / missing outline.
// Its label ("S1 · 3") shows only while the mouse is over the area.
function Area({ area, shape, keyword }) {
  const style = KEYWORD_MAP.levels[competitionLevel(area.value, area.searches)];
  const label = (
    <Tooltip direction="center" className={KEYWORD_MAP.label}>
      {area.postcode} · {area.searches === 0 ? "–" : area.value}
    </Tooltip>
  );
  const popup = <Popup><AreaDetail area={area} keyword={keyword} /></Popup>;
  if (shape.feature) {
    return <GeoJSON data={shape.feature} style={() => style}>{label}{popup}</GeoJSON>;
  }
  return <CircleMarker center={shape.point} radius={KEYWORD_MAP.pointRadius} pathOptions={style}>{label}{popup}</CircleMarker>;
}

function AreaDetail({ area, keyword }) {
  return (
    <div className="min-w-[11.25rem]">
      <p className={KEYWORD_MAP.popupTitle}>{area.postcode}{area.town ? ` · ${area.town}` : ""}</p>
      <p className={`${KEYWORD_MAP.popupMuted} mb-1.5`}>
        {keyword || "All keywords"} · {area.searches} search{area.searches === 1 ? "" : "es"}
      </p>
      {area.searches === 0 ? (
        <p className={KEYWORD_MAP.popupMuted}>No completed searches here.</p>
      ) : (
        CHANNELS.map(([id]) => (
          <div key={id} className={KEYWORD_MAP.popupRow}>
            <span>{CHANNEL_NAMES[id]}</span>
            <span className="font-semibold">{area.channels[id] === 0 ? "none" : `${area.channels[id]} advertiser${area.channels[id] === 1 ? "" : "s"}`}</span>
          </div>
        ))
      )}
    </div>
  );
}

// Loads outlines for the postcodes shown; remembered for the session.
function usePostcodeShapes(postcodes) {
  const [shapes, setShapes] = useState({});
  const [loading, setLoading] = useState(false);
  const key = [...postcodes].sort().join("|");

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setLoading(true);
    loadPostcodeShapes(key.split("|")).then(found => {
      if (cancelled) return;
      setShapes(current => ({ ...current, ...found }));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [key]);

  return { shapes, loading };
}

// Zooms to the areas when they change, and re-measures when the panel expands.
function FitToAreas({ shapes, expanded }) {
  const map = useMap();
  const bounds = L.latLngBounds([]);
  for (const s of shapes) {
    if (s.feature) bounds.extend(L.geoJSON(s.feature).getBounds());
    else bounds.extend(s.point);
  }
  const key = bounds.isValid() ? bounds.toBBoxString() : "";

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (key) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 13 });
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key, expanded]);

  return null;
}

function swatchStyle(level) {
  return {
    backgroundColor: level.fillColor,
    border: `1px ${level.dashArray ? "dashed" : "solid"} ${level.color}`,
  };
}
